import { Button, Input, TextArea, TextInput } from '@cimplico/components';
import { useNavigate, useParams } from 'react-router-dom';
import CategorySelect from '../../components/procedure-category-select/ProcedureCategorySelect';
import EntityTypeSelect from '../../components/entity-type-select/EntityTypeSelect';
import RoleSelect from '../../components/role-select/RoleSelect';
import { toastNotification } from '../../components/toast/Toast';
import { useFirmContext } from '../../providers/FirmProvider';
import { useUpdateProcedures } from '../../queries/use-update-procedures';
import { EntityTypeEnum } from '../../types/entity';
import { Procedure } from '../../types/procedure';
import { WorkpaperRoleEnum } from '../../types/workpaper';
import { useForm, Controller } from 'react-hook-form';
import { useIsActiveSubscription } from '../../hooks/use-is-active-subscription';

type EditTemplateProcedureFormProps = {
    procedure: Procedure;
};

type NewProcedureFormFields = {
    name: string;
    description: string;
    role: WorkpaperRoleEnum;
    categoryId?: string;
    entityTypes: EntityTypeEnum[];
};

const NAME_MAX_CHAR = 250;
const DESCRIPTION_MAX_CHAR = 2000;

function EditTemplateProcedureForm({ procedure }: EditTemplateProcedureFormProps): JSX.Element {
    const navigate = useNavigate();
    const { shortId } = useFirmContext();
    const { id: templateId } = useParams();
    const activeSubscription = useIsActiveSubscription();

    function handleCancel(): void {
        navigate(`..`);
    }

    const updateTemplateProcedureQuery = useUpdateProcedures(templateId as string);

    const {
        control,
        formState: { errors, isDirty, dirtyFields },
        setError,
        handleSubmit,
        getValues,
    } = useForm<NewProcedureFormFields>({
        defaultValues: {
            name: procedure.name,
            description: procedure.description,
            role: procedure.role,
            categoryId: procedure.category?.id,
            entityTypes: procedure.entityTypes,
        },
    });

    function onSubmit(): void {
        const values = getValues();

        if (values.name.length > NAME_MAX_CHAR) {
            setError('name', { message: `A procedure name must be within ${NAME_MAX_CHAR} characters.` });
            return;
        }

        if (values.description.length > DESCRIPTION_MAX_CHAR) {
            setError('description', { message: `A procedure description must be within ${DESCRIPTION_MAX_CHAR} characters.` });
            return;
        }

        if (!isDirty) {
            handleCancel();
            return;
        }

        updateTemplateProcedureQuery.mutate(
            [
                {
                    id: procedure.id,
                    fields: {
                        name: dirtyFields.name ? values.name : undefined,
                        description: dirtyFields.description ? values.description : undefined,
                        role: dirtyFields.role ? values.role : undefined,
                        categoryId: dirtyFields.categoryId ? values.categoryId || null : undefined,
                        entityTypes: dirtyFields.entityTypes ? values.entityTypes : undefined,
                    },
                },
            ],
            {
                onSuccess: () => {
                    navigate(`/${shortId}/templates/procedures/${templateId}`);
                    toastNotification('success', 'Procedure details updated!');
                },
                onError: (e) => {
                    const result = e.response?.data.results[0];

                    if (result.error?.fields?.name) {
                        setError('name', { type: 'custom', message: result.error.fields.name });
                    }

                    if (result.error?.fields?.description) {
                        setError('description', { type: 'custom', message: result.error.fields.description });
                    }

                    toastNotification('error', 'Unable to update template procedure!');
                },
            }
        );
    }

    return (
        <form className="flex flex-col gap-8" onSubmit={handleSubmit(onSubmit)}>
            {/* PROCEDURE NAME */}
            <Controller
                name="name"
                rules={{ required: 'A name is required.' }}
                control={control}
                render={({ field }): JSX.Element => (
                    <TextInput
                        placeholder="e.g. Clear suspense account and all queries"
                        title="Procedure name"
                        isError={Boolean(errors.name)}
                        onChange={(value): void => field.onChange(value)}
                        errorMessage={errors?.name?.message?.toString()}
                        characterLimit={NAME_MAX_CHAR}
                        defaultValue={field.value}
                        isDisabled={!activeSubscription}
                        autoFocus
                    />
                )}
            />

            {/* PROCEDURE DESCRIPTION */}
            <Controller
                name="description"
                rules={{ required: 'A description is required.' }}
                control={control}
                render={({ field }): JSX.Element => (
                    <TextArea
                        title="Add a description"
                        placeholder="Description..."
                        errorMessage={errors?.description?.message?.toString()}
                        onChange={(value): void => field.onChange(value)}
                        defaultValue={field.value}
                        isDisabled={!activeSubscription}
                        characterLimit={DESCRIPTION_MAX_CHAR}
                    />
                )}
            />

            {/* ROLE TYPE SELECT */}
            <Controller
                name="role"
                rules={{ required: 'A role is required.' }}
                control={control}
                render={({ field: { ref, onChange, value } }): JSX.Element => (
                    <RoleSelect
                        innerRef={ref}
                        title="Role"
                        placeholder="Select a role..."
                        errorMessage={errors?.role?.message?.toString()}
                        onChange={onChange}
                        defaultRole={value}
                        isDisabled={!activeSubscription}
                    />
                )}
            />

            {/* CATEGORY SELECT */}
            {templateId && (
                <Controller
                    name="categoryId"
                    control={control}
                    render={({ field: { ref, onChange, value } }): JSX.Element => (
                        <CategorySelect
                            innerRef={ref}
                            isOptional
                            defaultCategory={value}
                            title="Category"
                            onChange={(v): void => onChange(v[0])}
                            templateId={templateId}
                            isDisabled={!activeSubscription || updateTemplateProcedureQuery.isLoading}
                        />
                    )}
                />
            )}

            {/* ENTITY TYPE SELECT */}
            <Controller
                name="entityTypes"
                control={control}
                render={({ field: { ref, onChange, value } }): JSX.Element => (
                    <EntityTypeSelect
                        innerRef={ref}
                        isMulti
                        isOptional
                        defaultEntityType={value}
                        placeholder="Select entity types..."
                        title="Entity Type"
                        onChange={onChange}
                        isDisabled={!activeSubscription || updateTemplateProcedureQuery.isLoading}
                    />
                )}
            />

            <div className="flex justify-end gap-4">
                <Button type="hollow" label="Cancel" onClick={handleCancel} />
                <Button
                    type="primary"
                    label={updateTemplateProcedureQuery.isLoading ? 'Saving...' : 'Save'}
                    disabled={!activeSubscription || updateTemplateProcedureQuery.isLoading}
                    isLoading={updateTemplateProcedureQuery.isLoading}
                    isSubmit
                />
            </div>
        </form>
    );
}

export default EditTemplateProcedureForm;
